import * as Globals from "../../globals";
import {WorkingMeasurements} from "./workingMeasurements";
import {Measurements} from "./measurements";
import {OutputMeasurements} from "./outputMeasurements";
import {buildingGroup, measurementsGroup} from "../../main";
import {setObjectLayer} from "../../utils/layerUtils";
import {render, sceneCamera, setBeforeSceneRender} from "../../sceneManager";
import {Box3, DoubleSide, Euler, Group, Mesh, MeshBasicMaterial, PlaneGeometry, RingGeometry, Vector2, Vector3} from "three";
import {findChildrenByName, setBox3FromObjectArray} from "../../utils/threeUtils";
import {supportBeamMeshName, wallGroupName, floorGroupName, roofGroupName} from "../../constants/nameConstants";
import {toFixedFloat} from "../../utils/utils";
import {floorConstructionHeight} from "../../globals";
import {frameBuilding} from "../../utils/cameraUtils";
import {Text} from "../baseElements/text";

export const measurementVariants = {
    working: 1,
    output: 2
};
export let measurementVariant = measurementVariants.working;

//Erstellt alle Arbeitsbemaßungen für die aktuelle Zeichnung
export async function generateMeasurements() {
    Measurements.clearMeasurements();

    if (measurementVariant === measurementVariants.working) {
        new WorkingMeasurements();
    } else {
        new OutputMeasurements();
    }

    addWallHeightLabels();
    setObjectLayer(measurementsGroup, Globals.layers.measurements, true);
    markHeightLabelsForScene();
    frameBuilding();
    render();
}

export function refreshWallHeightLabels() {
    removeWallHeightLabels();
    addWallHeightLabels();
    setObjectLayer(measurementsGroup, Globals.layers.measurements, true);
    markHeightLabelsForScene();
    render();
}

function markHeightLabelsForScene() {
    for (let i = 0; i < measurementsGroup.children.length; i++) {
        let child = measurementsGroup.children[i];
        if (child.name === "wallHeightLabel") {
            setObjectLayer(child, Globals.layers.sceneViewOnly, true);
        }
    }
}

function removeWallHeightLabels() {
    for (let i = measurementsGroup.children.length - 1; i >= 0; i--) {
        let child = measurementsGroup.children[i];
        if (child.name !== "wallHeightLabel") {
            continue;
        }
        child.traverse(object => {
            if (object.geometry) {
                object.geometry.dispose();
            }
        });
        child.removeFromParent();
    }
}

const heightFontSize = 0.15;
const heightLineThickness = 0.01;
const heightCircleDiameter = 0.05;
const heightCircleThickness = 0.01;
const heightTextGap = 0.05;
const heightLineOverhang = 0.1;
const heightTickLength = 0.4;
const heightLineMaterial = new MeshBasicMaterial({color: 0x000000, side: DoubleSide});

function addWallHeightLabels() {
    let walls = findChildrenByName(buildingGroup, wallGroupName, false, true) || [];
    let beams = findChildrenByName(buildingGroup, supportBeamMeshName, false, true) || [];
    let elements = walls.concat(beams);
    if (elements.length === 0) {
        return;
    }

    let bounds = setBox3FromObjectArray(elements);
    if (bounds.isEmpty() || bounds.max.y < 0.2) {
        return;
    }

    // Fußbodenaufbau steckt in der Wandhöhe und zählt nicht zur lichten Höhe.
    // Bodenelement, Dachstärke und Attika liegen sichtbar darüber oder darunter und zählen mit.
    let top = structureTop(bounds.max.y);
    let floorLevel = Math.min(Math.max(floorConstructionHeight, 0), bounds.max.y);
    let underside = floorElementUnderside();
    let height = toFixedFloat(top - underside - floorLevel);
    if (height < 0.05) {
        return;
    }
    let startY = top - height;
    let corners = outerCorners(elements);
    if (corners.length === 0) {
        return;
    }

    let scale = measurementScale(bounds);
    let center = new Vector2();
    for (let i = 0; i < corners.length; i++) {
        center.x += corners[i].x;
        center.y += corners[i].y;
    }
    center.multiplyScalar(1 / corners.length);

    for (let i = 0; i < corners.length; i++) {
        let corner = corners[i];
        let outward = new Vector2(corner.x - center.x, corner.y - center.y);
        if (outward.lengthSq() < 1e-6) {
            outward.set(1, 0);
        }
        outward.normalize();

        let group = createCornerHeightLabel(formatHeight(height), height, scale);
        let push = (heightTickLength * 0.25 + 0.12) * scale;
        group.position.set(corner.x + outward.x * push, startY, corner.y + outward.y * push);
        measurementsGroup.addWithUuid(group);
    }

    faceHeightLabelsToCamera();
}

function structureTop(wallTop) {
    let top = wallTop;
    let roofs = findChildrenByName(buildingGroup, roofGroupName, false, true) || [];

    for (let i = 0; i < roofs.length; i++) {
        let box = new Box3().setFromObject(roofs[i]);
        if (!box.isEmpty() && box.max.y > top) {
            top = box.max.y;
        }
    }

    return top;
}

function floorElementUnderside() {
    let floors = findChildrenByName(buildingGroup, floorGroupName, false, true) || [];
    let lowest = 0;

    for (let i = 0; i < floors.length; i++) {
        let declared = floors[i].userData?.floor?.thickness;
        if (typeof declared === "number" && declared > 0) {
            lowest = Math.min(lowest, -declared);
            continue;
        }

        let box = new Box3().setFromObject(floors[i]);
        if (!box.isEmpty() && box.min.y < lowest) {
            lowest = box.min.y;
        }
    }

    return lowest;
}

function formatHeight(height) {
    let value = toFixedFloat(height);
    if (Number.isInteger(value)) {
        value = value.toFixed(2);
    }
    return value.toString();
}

function measurementScale(bounds) {
    let size = bounds.getSize(new Vector3());
    let scale = 1;
    if (size.x > 10 || size.z > 10) {
        scale = Math.max(toFixedFloat(size.x / 10, 1), toFixedFloat(size.z / 10, 1));
    }
    return Math.min(scale, 3);
}

function createCornerHeightLabel(label, height, scale) {
    let group = new Group();
    group.name = "wallHeightLabel";

    let fontSize = heightFontSize * scale;
    let thickness = heightLineThickness * scale;
    let text = new Text(label, fontSize, "center", "middle", new Vector3(0, height / 2, 0.002), new Euler(0, 0, Math.PI / 2), null, 0x000000, false);
    group.addWithUuid(text);

    addHeightTick(group, 0, scale);
    addHeightTick(group, height, scale);
    addHeightRing(group, 0, scale);
    addHeightRing(group, height, scale);

    text.sync(() => {
        let bounds = text._textRenderInfo ? text._textRenderInfo.visibleBounds : null;
        let half = bounds ? (bounds[2] - bounds[0]) / 2 : fontSize * 1.2;
        let gap = heightTextGap * scale;
        let center = height / 2;
        addHeightLine(group, -heightLineOverhang * scale, center - half - gap, thickness);
        addHeightLine(group, center + half + gap, height + heightLineOverhang * scale, thickness);
        setObjectLayer(group, Globals.layers.sceneViewOnly, true);
        render();
    });

    return group;
}

function addHeightLine(group, y0, y1, thickness) {
    if (y1 - y0 <= 0.01) {
        return;
    }
    let mesh = new Mesh(new PlaneGeometry(thickness, y1 - y0), heightLineMaterial);
    mesh.position.y = (y0 + y1) / 2;
    group.addWithUuid(mesh);
}

function addHeightTick(group, y, scale) {
    let mesh = new Mesh(new PlaneGeometry(heightTickLength * scale, heightLineThickness * scale), heightLineMaterial);
    mesh.position.y = y;
    group.addWithUuid(mesh);
}

function addHeightRing(group, y, scale) {
    let outer = heightCircleDiameter * scale / 2;
    let inner = Math.max(outer - heightCircleThickness * scale, outer * 0.4);
    let mesh = new Mesh(new RingGeometry(inner, outer, 24), heightLineMaterial);
    mesh.position.y = y;
    group.addWithUuid(mesh);
}

function faceHeightLabelsToCamera() {
    if (!sceneCamera) {
        return;
    }

    for (let i = 0; i < measurementsGroup.children.length; i++) {
        let label = measurementsGroup.children[i];
        if (label.name !== "wallHeightLabel") {
            continue;
        }
        let dx = sceneCamera.position.x - label.position.x;
        let dz = sceneCamera.position.z - label.position.z;
        if (dx * dx + dz * dz < 1e-8) {
            continue;
        }
        label.rotation.y = Math.atan2(dx, dz);
    }
}

setBeforeSceneRender(faceHeightLabelsToCamera);

function outerCorners(elements) {
    let points = [];
    for (let i = 0; i < elements.length; i++) {
        let box = new Box3().setFromObject(elements[i]);
        points.push(new Vector2(box.min.x, box.min.z));
        points.push(new Vector2(box.max.x, box.min.z));
        points.push(new Vector2(box.max.x, box.max.z));
        points.push(new Vector2(box.min.x, box.max.z));
    }

    let hull = convexHull(points);
    return simplifyCorners(hull, 0.45);
}

function simplifyCorners(points, minDistance) {
    let kept = [];
    for (let i = 0; i < points.length; i++) {
        let point = points[i];
        let duplicate = false;
        for (let j = 0; j < kept.length; j++) {
            if (kept[j].distanceTo(point) < minDistance) {
                duplicate = true;
                break;
            }
        }
        if (!duplicate) {
            kept.push(point);
        }
    }
    return kept;
}

function convexHull(points) {
    let unique = [];
    for (let i = 0; i < points.length; i++) {
        let point = points[i];
        let exists = false;
        for (let j = 0; j < unique.length; j++) {
            if (Math.abs(unique[j].x - point.x) < 0.02 && Math.abs(unique[j].y - point.y) < 0.02) {
                exists = true;
                break;
            }
        }
        if (!exists) {
            unique.push(point);
        }
    }

    unique.sort((a, b) => a.x === b.x ? a.y - b.y : a.x - b.x);
    if (unique.length <= 2) {
        return unique;
    }

    let cross = (o, a, b) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
    let lower = [];
    for (let i = 0; i < unique.length; i++) {
        while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], unique[i]) <= 0) {
            lower.pop();
        }
        lower.push(unique[i]);
    }

    let upper = [];
    for (let i = unique.length - 1; i >= 0; i--) {
        while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], unique[i]) <= 0) {
            upper.pop();
        }
        upper.push(unique[i]);
    }

    upper.pop();
    lower.pop();
    return lower.concat(upper);
}
